import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
} from "@angular/core";
import { SmartFormPoolFacade } from "../store/facade";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { SmartFormFolderFlatNode, SmartFormFolderNode } from "@vhealth/engage";
import { SmartFormFolder } from "../store";
import { FormControl, FormGroup } from "@angular/forms";
import { FlatTreeControl } from "@angular/cdk/tree";
import { MatTreeFlatDataSource, MatTreeFlattener } from "@angular/material/tree";
import { MatDialog } from "@angular/material/dialog";
import { TranslocoService } from "@jsverse/transloco";
import { FuseConfirmationService } from "@nx-workspace/fuse";
import { take, takeUntil } from "rxjs/operators";
import { CreateFolderComponent } from "../create-folder/create-folder.component";
import { EditFolderComponent } from "../edit-folder/edit-folder.component";

@Component({
    selector: "form-folder-picker",
    templateUrl: "./form-folder-picker.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FolderPickerComponent implements OnDestroy {
    private _unsubscribeAll = new Subject();

    isLoading$: Observable<boolean>;

    folders$: Observable<SmartFormFolder[]>;

    isThereAnyData$: Observable<boolean>;

    filteredSmartFormFolders$: ReplaySubject<SmartFormFolder[]> = new ReplaySubject<SmartFormFolder[]>(1);

    smartFormFolders: SmartFormFolderNode[] = [];

    flatNodeMap: Map<SmartFormFolderFlatNode, SmartFormFolderNode> = new Map();

    nestedNodeMap: Map<string, SmartFormFolderFlatNode> = new Map();

    searchForm: FormGroup = new FormGroup({
        text: new FormControl(""),
    });

    private _transformer = (node: SmartFormFolderNode, level) => {
        const flatNode = new SmartFormFolderFlatNode();
        flatNode.level = level;
        flatNode.expandable = node.children && node.children?.length > 0;
        flatNode.title = node.title;
        flatNode.parentId = node.parentId;
        flatNode.data = node.data;
        this.flatNodeMap.set(flatNode, node);
        this.nestedNodeMap.set(node.title, flatNode);
        return flatNode;
    };

    treeControl = new FlatTreeControl<SmartFormFolderFlatNode>(
        (node) => node.level,
        (node) => node.expandable
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children
    );

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    hasChild = (_: number, node: SmartFormFolderFlatNode) => node.expandable;

    @Output()
    onFolderSelected = new EventEmitter<SmartFormFolder>();

    @Input()
    selected: SmartFormFolder;

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _matDialog: MatDialog,
        private _translateService: TranslocoService,
        private _facade: SmartFormPoolFacade,
        private _confirmationService: FuseConfirmationService
    ) {
        this.folders$ = this._facade.folders$;
        this.isThereAnyData$ = this._facade._isThereAnyData$;

        //this._facade.root();
    }

    ngOnInit(): void {
        this.filteredSmartFormFolders$.pipe(takeUntil(this._unsubscribeAll)).subscribe((folders) => {
            this.smartFormFolders = folders.map((folder) => {
                return new SmartFormFolderNode(folder, folder.parentFolderId ?? 0);
            });
            this.dataSource.data = this.smartFormFolders;
            this.treeControl.expandAll();
            this._changeDetector.markForCheck();
        });

        this.folders$.pipe(takeUntil(this._unsubscribeAll)).subscribe((folders) => {
            this.filteredSmartFormFolders$.next(folders);
        });

        this.searchForm
            .get("text")
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((text) => {
                this.applyFilter(text.toLowerCase());
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    reload(nodes: SmartFormFolderNode[]) {
        nodes.forEach((node) => {
            if (node.children) {
                this.reload(node.children);
            }
        });
    }

    map(items: SmartFormFolder[], parentId: number | null = null): SmartFormFolderNode[] {
        return items.map((item) => {
            const node = new SmartFormFolderNode(item, parentId);
            node.children = this.map(item.folders, item.parentFolderId);
            return node;
        });
    }

    createFolder(parentId: number = null) {
        //Without the timeout the text inside the Dialog will not gain the focus
        setTimeout(() => {
            this._matDialog.open(CreateFolderComponent, {
                panelClass: "dialog-v2",
                data: {
                    parentId,
                },
            });
        });
    }

    editFolder(event: any, folder: SmartFormFolderFlatNode) {
        //Without the timeout the text inside the Dialog will not gain the focus
        setTimeout(() => {
            this._matDialog.open(EditFolderComponent, {
                panelClass: "dialog-v2",
                data: folder.data,
            });
        });
    }

    deleteFolder(event: any, folder: SmartFormFolderFlatNode) {
        //Without the timeout the text inside the Dialog will not gain the focus
        setTimeout(() => {
            this._confirmationService
                .open({
                    title: this._translateService.translate("health.engage.form-pool.folders.delete.alert.title"),
                    message: this._translateService.translate("health.engage.form-pool.folders.delete.alert.message", {
                        name: folder.data.name,
                    }),
                    actions: {
                        confirm: {
                            label: this._translateService.translate("ACTION_DELETE"),
                        },
                        cancel: {
                            label: this._translateService.translate("ACTION_CANCEL"),
                        },
                    },
                })
                .afterClosed()
                .subscribe((result) => {
                    if (result === "confirmed") {
                        this._facade.deleteFolder(folder.data.id);
                    }
                });
        });
    }

    navigate(folder: SmartFormFolder, loadSmartForms: boolean = false) {
        this._facade.navigate(folder, loadSmartForms);
    }

    goBack() {
        this._facade.goBack();
    }

    applyFilter(text: string) {
        this.folders$.pipe(take(1)).subscribe((folders) => {
            let filteredFolders = this.filterFoldersByName(folders, text);
            this.filteredSmartFormFolders$.next(filteredFolders);
        });
    }

    filterFoldersByName(folders: SmartFormFolder[], searchText: string) {
        let filteredFolders = [];
        folders.forEach((folder) => {
            if (folder.name.toLowerCase().includes(searchText)) {
                filteredFolders.push(folder);
            } else if (folder.folders) {
                let filteredSubFolders = this.filterFoldersByName(folder.folders, searchText);
                if (filteredSubFolders.length > 0) {
                    let filteredFolder = Object.assign({}, folder);
                    filteredFolder.folders = filteredSubFolders;
                    filteredFolders.push(filteredFolder);
                }
            }
        });
        return filteredFolders;
    }

    select(folder: SmartFormFolder) {
        this.onFolderSelected.emit(folder);
    }
}
